import { createSignal, onMount, createEffect, Show, on, untrack, createMemo, onCleanup } from "solid-js";
import { useActiveManeuverViewMirror } from "../../../../../store/activeManeuverViewStore";

import DropDownButton from "../../../../../components/buttons/DropDownButton";

import DataTable_Big from "../../../../../components/maneuvers/standard/DataTable_Big";
import DataTable_Small from "../../../../../components/maneuvers/standard/DataTable_Small";
import Map from "../../../../../components/maneuvers/standard/Map";
import Scatter from "../../../../../components/maneuvers/standard/Scatter";
import TimeSeries from "../../../../../components/maneuvers/standard/TimeSeries";
import { clearManeuverPlayhead } from "../../../../../store/playbackStore";
import DataTable_BigGrouped from "../../../../../components/maneuvers/grouped/DataTable_Big";
import DataTable_SmallGrouped from "../../../../../components/maneuvers/grouped/DataTable_Small";
import MapGrouped from "../../../../../components/maneuvers/grouped/Map";
import ScatterGrouped from "../../../../../components/maneuvers/grouped/Scatter";
import TimeSeriesGrouped from "../../../../../components/maneuvers/grouped/TimeSeries";
import ScatterTimeseries from "../../../../../components/charts/ScatterTimeseries";
import * as d3 from "d3";

import { setupMediaContainerScaling } from "../../../../../utils/global";
import { logPageLoad, logActivity, getCurrentProjectDatasetIds } from "../../../../../utils/logging";
import { error as logError, debug as logDebug } from "../../../../../utils/console";
import { splitMetadataFilterTokens } from "../../../../../utils/metadataFilterTokens";
import { appendCrewCodeToFilters } from "@utils/crewFilter";
import { normalizeManeuverEventType } from "../../../../../utils/maneuverEventType";
import { clearManeuverSelectionOnEventTypeChange } from "../../../../../utils/maneuverEventTypeSelection";
import { useDashboardShareRestore, willRestoreShare, shareRestoreReady } from "../../../../../hooks/useDashboardShareRestore";
import { useManeuverStatesForEventType } from "../../../../../utils/maneuverStateReconcile";
import { buildManeuversShareState, applyManeuversShareState, currentManeuverBaselineShare, consumeShareSpecifiedBaseline } from "../../../../../utils/reportShareState";

import { phase, setPhase, color, setColor, tws, setTws, eventType, setEventType, grouped, groupDisplayMode, setGroupDisplayMode, maneuvers, setManeuvers, setTableData, setFiltered, filtered, tabledata, maneuverTableSort, resetManeuverTableSort } from "../../../../../store/globalStore";
import { sortManeuverRows } from "../../../../../utils/maneuverTableSort";
import { selectedEvents, setSelectedEvents, setLegendSelectionActive, setTriggerUpdate, setTriggerSelection, setSelection, selection, hasSelection, isCut, cutEvents, hideSelectedEvents, selectedGroupKeys, setSelectedGroupKeys, setSelectedRanges, selectedRanges, clearSelection, cutSelection, setHasSelection } from "../../../../../store/selectionStore";
import {
  buildSelectedRangesForEvents,
  selectionRangesSignatureForEvents,
  extractRowSourceName,
  type SelectionRangeColorResolver,
} from "../../../../../utils/selectionRangeHelpers";

import { persistantStore } from "../../../../../store/persistantStore";
import { sourcesStore } from "../../../../../store/sourcesStore";
import { apiEndpoints } from "@config/env";
import { getData, getEventTimes, formatDateTime } from "../../../../../utils/global";
import { runEventTimesSelectionSync } from "../../../../../utils/eventTimesSelection";
import ManeuverSettings from "../../../../../components/menus/ManeuverSettings";
import PerformanceFilterSummary from "../../../../../components/legends/PerformanceFilterSummary";
import { persistentSettingsService } from "../../../../../services/persistentSettingsService";
import { getProjectManeuverFilters } from "../../../../../services/projectFiltersService";
import { user } from "../../../../../store/userStore";
import {
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  setSelectedSources as setFilterStoreSelectedSources,
  setSelectedGradesManeuvers,
  setSelectedStatesManeuvers,
  setSelectedRacesManeuvers,
  setSelectedLegsManeuvers,
  maneuverTrainingRacing,
  setManeuverTrainingRacing,
  maneuverTackEntry,
  setManeuverTackEntry,
  raceOptions as storeRaceOptions,
  legOptions as storeLegOptions,
  gradeOptions as storeGradeOptions,
  setRaceOptions as pushRaceOptionsToFilterStore,
  setLegOptions as pushLegOptionsToFilterStore,
  setGradeOptions as pushGradeOptionsToFilterStore,
} from "../../../../../store/filterStore";
import { getGroupKeyFromItem, groupKeyEquals, legendTextToGroupKeyTable, toggleGroupKeyInList } from "../../../../../utils/colorGrouping";
import { TAKEOFF_CHANNELS, ACCELERATION_ACCMAX_CHANNELS, ACCELERATION_BUILD_VMG_CHANNELS, RIDE_HEIGHT_CHANNELS, buildManeuversChannelsRequest } from "../../../../../utils/maneuversConfig";
import {
  mapManeuverRowsToScatterTimeseriesPoints,
  maneuverTimelineLegendGroups,
} from "../../../../../utils/maneuverScatterTimeseries";
import ManeuverColorLegend from "../../../../../components/legends/ManeuverColorLegend";
import {
  buildManeuverColorLegendItems,
  applyManeuverLegendSelection,
} from "../../../../../utils/maneuverColorLegend";
import { maneuverColorLegendReservePx } from "../../../../../utils/maneuverLegendHeight";
import { maneuverBaselineLabel } from "../../../../../utils/maneuverBaselineLegend";
import { maneuverBaselineStore } from "../../../../../store/maneuverBaselineStore";
import { applyManeuverEntrySide } from "../../../../../utils/maneuverTackEntryFilter";
import { passesManeuverRaceFilter, passesManeuverTrainingRacingFilter } from "../../../../../utils/maneuverRaceFilter";
import { applyManeuverFoilingStateAfterFetch, MANEUVER_DEFAULT_STATE_OPTIONS, reconcileManeuverStatesWithBaseline } from "../../../../../utils/maneuverFoilingState";
import { fetchHistoryManeuverStateOptions } from "../../../../../utils/maneuverStateOptionsService";
import { useBaselineSave } from "../../../../../hooks/useBaselineSave";
import BaselineSaveModal from "../../../../../components/modals/BaselineSaveModal";
import BaselineDisplayModal from "../../../../../components/modals/BaselineDisplayModal";
import { useManeuverBaselineDisplay } from "../../../../../hooks/useManeuverBaselineDisplay";
import { useManeuverBaselineLoader } from "../../../../../utils/maneuverBaseline";

const { selectedClassName, selectedProjectId, selectedDate, selectedDatasetId, selectedYear, selectedEvent } = persistantStore;

// Channels list for maneuvers endpoints - matches single-source Maneuvers / ManeuversHistory (incl. drop columns)
const MANEUVERS_CHANNELS = [
  'Tws_bin',
  'Tws_avg',
  'Twa_entry',
  'Twa_entry_n',
  'Vmg_perc_avg',
  'Loss_inv_tgt',
  'Loss_turn_tgt',
  'Loss_build_tgt',
  'Loss_total_tgt',
  'Mmg',
  'Bsp_drop',
  'Bsp_min',
  'Bsp_min_delta',
  'Drop_time',
  'Pop_time',
  'Time_two_boards',
  'Raise_time',
  'Accel_slope',
  'Decel_slope',
  'Lwy_max',
  'Turn_radius',
  'Accel_max',
  'Accel_min',
  'Twa_exit_n',
  'Twa_build_n',
  'Twa_drop_n',
  'Turn_rate_max',
  'Turn_angle_max',
  'Overshoot_angle',
  'Overshoot_perc',
  'Time_raising',
  'Time_dropping',
  'Rud_ang_max',
  'Twd_delta',
  'Rake_drop',
  'Aoa_drop',
  'Heel_drop',
  'Rake_aoa_min_old_turn',
  'Aoa_raise',
  'Pitch_raise',
  'Heel_lock',
  'Rake_min_old_turn',
  'Rake_max_new_turn',
  'Rake_removed_old_time_turn',
  'Rake_added_new_time_turn',
  'Cant_drop_tgt',
  ...ACCELERATION_BUILD_VMG_CHANNELS,
  ...ACCELERATION_ACCMAX_CHANNELS,
  ...RIDE_HEIGHT_CHANNELS,
];

export default function FleetManeuversHistoryPage() {
  // Use fleet context for this component
  const context = 'fleet';
  
  const [eventTypes] = createSignal<string[]>(['TACK','GYBE','ROUNDUP','BEARAWAY','TAKEOFF']);
  const [phases] = createSignal<string[]>(['FULL','INVESTMENT','TURN','ACCELERATION']);
  // For fleet context, allow coloring by SOURCE, TACK, CONFIG, CREW, STATE, and EVENT
  const [colors] = createSignal<string[]>(['SOURCE', 'TACK', 'CONFIG', 'STATE', 'EVENT', 'TESTS']);
  const [twsoptions, setTwsOptions] = createSignal<string[]>([]);

  const [views] = createSignal<string[]>(['MAP','TABLE','SCATTER','TIME SERIES']);
  const [view, setView] = createSignal<'MAP' | 'TABLE' | 'SCATTER' | 'TIME SERIES'>('MAP');
  useActiveManeuverViewMirror(view);

  // '+' saves the lassoed maneuvers to a maneuver baseline (see ManeuversHistory for the rules;
  // the server derives CONFIG and maneuver type per member and fits every numeric channel).
  const baselineDisplay = useManeuverBaselineDisplay({
    getClassName: () => selectedClassName(),
    getProjectId: () => selectedProjectId(),
    getEventType: () => eventType(),
    onApplied: () => setTriggerUpdate(true),
  });
  useManeuverBaselineLoader({ tws, eventType, context });

  const baselineSave = useBaselineSave({
    kind: 'maneuver',
    source: 'fleet-maneuvers-history',
    getClassName: () => selectedClassName(),
    getProjectId: () => selectedProjectId(),
    getVisibleEventIds: () => filtered(),
    getSaveBlockReason: () =>
      grouped() ? 'Set GROUPED to OFF to save maneuvers to a baseline.' : null,
    onSaved: ({ baselineId, name }) => baselineDisplay.refreshAfterSave(baselineId, name),
  });
  // MAP mode: toggle the left column between the data table and a playhead-synced timeseries whose
  // drag moves a boat on the map. Ephemeral — reset back to TABLE whenever we leave MAP.
  const [mapSidePanel, setMapSidePanel] = createSignal<'TABLE' | 'CHART'>('TABLE');
  createEffect(on(view, (v) => { if (v !== 'MAP') { setMapSidePanel('TABLE'); clearManeuverPlayhead(); } }));
  createEffect(() => { if (mapSidePanel() === 'CHART' && selectedEvents().length === 0) { setMapSidePanel('TABLE'); } });
  const [groupedOptions] = createSignal<string[]>(['OFF','ON','MIX']);
  const [scatterKey, setScatterKey] = createSignal<number>(0);

  // Table view: toggle + label from DataTable_Big / DataTable_BigGrouped
  type TableFormattingControl = { toggle: () => void; hasFormatting: boolean };
  const [formattingControl, setFormattingControl] = createSignal<TableFormattingControl | null>(null);

  const viewOptions = createMemo<string[]>(() => {
    return grouped() ? ['MAP','TABLE','BOXES','TIME SERIES'] : views();
  });

  createEffect(() => {
    if (view() !== "TABLE") {
      setFormattingControl(null);
    }
  });

  // State for ManeuverSettings
  const [raceOptions, setRaceOptions] = createSignal<(number | string)[]>([]);
  const [legOptions, setLegOptions] = createSignal<number[]>([]);
  const [gradeOptions, setGradeOptions] = createSignal<number[]>([]);
  const [stateOptions, setStateOptions] = createSignal<string[]>([...MANEUVER_DEFAULT_STATE_OPTIONS]);
  const [selectedRaces, setSelectedRaces] = createSignal<(number | string)[]>([]);
  const [selectedLegs, setSelectedLegs] = createSignal<number[]>([]);
  const [selectedGrades, setSelectedGrades] = createSignal<number[]>([]);
  const [selectedStates, setSelectedStates] = createSignal<string[]>([]);
  /**
   * True when the maneuver type on screen has a REMEMBERED state selection, which is what stops
   * option discovery replacing it with "every state this type has". Owned by
   * useManeuverStatesForEventType below — the answer is per event type, because the vocabularies are
   * disjoint (H-ladder vs round up / bear away geometry).
   */
  let hasSavedStateFilter = false;

  const { persistStates, applyProjectDefaultStates } = useManeuverStatesForEventType({
    eventType,
    selectedStates,
    applySelection: (next) => {
      setSelectedStates(next);
      setSelectedStatesManeuvers(next);
    },
    setHasSavedStates: (value) => { hasSavedStateFilter = value; },
    userId: () => user()?.user_id ?? '',
    className: () => selectedClassName() ?? '',
    projectId: () => selectedProjectId() ?? 0,
    suppressInitialRestore: () => willRestoreShare('FleetManeuversHistory'),
  });
  
  // Project-specific filter state (Year, Event, Config) - same as FleetPerformanceHistory
  const [filterYear, setFilterYear] = createSignal('');
  const [filterEvent, setFilterEvent] = createSignal('');
  const [filterConfigValue, setFilterConfigValue] = createSignal('');
  const [filterCrew, setFilterCrew] = createSignal('');
  
  // Source selection state
  const [selectedSources, setSelectedSources] = createSignal<Set<number>>(new Set());

  const [showTimeline, setShowTimeline] = createSignal<boolean>(true);
  const handleTimelineChange = (value: boolean) => {
    setShowTimeline(value);
  };

  const FLEET_MANEUVERS_HISTORY_PAGE_KEY = 'FleetManeuversHistory';
  useDashboardShareRestore(
    FLEET_MANEUVERS_HISTORY_PAGE_KEY,
    () =>
      buildManeuversShareState({
        ...currentManeuverBaselineShare(),
        view: view(),
        showTimeline: showTimeline(),
        eventType: eventType(),
        phase: phase(),
        color: color(),
        tws: tws(),
        grouped: grouped(),
        groupDisplayMode: groupDisplayMode(),
        selectedRaces: selectedRaces(),
        selectedLegs: selectedLegs(),
        selectedGrades: selectedGrades(),
        selectedStates: selectedStates(),
        selectedSourceIds: Array.from(selectedSources()),
        maneuverTrainingRacing: maneuverTrainingRacing(),
        maneuverTackEntry: maneuverTackEntry(),
        filterYear: filterYear(),
        filterEvent: filterEvent(),
        filterConfigValue: filterConfigValue(),
        startDate: startDate(),
        endDate: endDate(),
        selectedEvents: selectedEvents(),
        hasSelection: hasSelection(),
        maneuverSelection: selection(),
      }),
    (obj) =>
      applyManeuversShareState(obj, FLEET_MANEUVERS_HISTORY_PAGE_KEY, {
        setView,
        setShowTimeline,
        setSelectedRaces,
        setSelectedLegs,
        setSelectedGrades,
        setSelectedStates,
        setSelectedSourceIds: (value: unknown) => setSelectedSources(value as Set<number>),
        setFilterYear,
        setFilterEvent,
        setFilterConfigValue,
        setStartDate,
        setEndDate,
        applySharedBaseline: (value: unknown) => { void baselineDisplay.showSharedBaseline(value); },
      })
  );

  const fleetManeuverSourceColorScale = createMemo(() => {
    const sources = sourcesStore.sources();
    const isReady = sourcesStore.isReady();
    if (!isReady || sources.length === 0) return null;
    const sourceNames = sources.map((s) => String(s.source_name).toLowerCase());
    const colorsArr = sources.map((s) => sourcesStore.getSourceColor(s.source_name) || '#1f77b4');
    return d3.scaleOrdinal<string, string>().domain(sourceNames).range(colorsArr).unknown('#1f77b4');
  });

  // Abort controllers for canceling requests
  let tableDataController: AbortController | null = null;
  let timesController: AbortController | null = null;
  let stateOptionsController: AbortController | null = null;

  const resolveFleetHistoryDateRange = (): { startDate: string; endDate: string } => {
    const filterStoreStart = startDate();
    const filterStoreEnd = endDate();
    const date = selectedDate();
    if (filterStoreStart && filterStoreEnd) {
      return { startDate: filterStoreStart, endDate: filterStoreEnd };
    }
    if (date) {
      return { startDate: date, endDate: date };
    }
    return { startDate: '2020-01-01', endDate: '2099-12-31' };
  };

  const resolveFleetHistorySourceNames = (): string[] => {
    const selectedSourceIds = selectedSources();
    const sources = sourcesStore.sources() ?? [];
    const names: string[] = [];
    sources.forEach((source: { source_id?: number; source_name?: string }) => {
      if (selectedSourceIds.size === 0 || (source.source_id != null && selectedSourceIds.has(source.source_id))) {
        if (source.source_name) names.push(source.source_name);
      }
    });
    return names;
  };

  const applyDiscoveredStateOptions = (discovered: string[]) => {
    if (discovered.length === 0) return;
    setStateOptions(discovered);
    const reconciled = reconcileManeuverStatesWithBaseline(
      selectedStates(),
      discovered,
      hasSavedStateFilter
    );
    if (
      reconciled.length !== selectedStates().length ||
      reconciled.some((s, i) => s !== selectedStates()[i])
    ) {
      setSelectedStates(reconciled);
      setSelectedStatesManeuvers(reconciled);
    }
  };

  const refreshManeuverStateOptions = async () => {
    const requestEventType = (eventType() || 'TACK').trim() || 'TACK';
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const sourceNames = resolveFleetHistorySourceNames();
    if (!className || !projectId || sourceNames.length === 0) return;

    const { startDate: startDateValue, endDate: endDateValue } = resolveFleetHistoryDateRange();

    if (stateOptionsController) stateOptionsController.abort();
    stateOptionsController = new AbortController();

    const discovered = await fetchHistoryManeuverStateOptions({
      className,
      projectId,
      sourceNames,
      startDate: startDateValue,
      endDate: endDateValue,
      eventType: requestEventType,
      count: 10,
      signal: stateOptionsController.signal,
    });
    if ((eventType() || 'TACK').trim() !== requestEventType) return;
    applyDiscoveredStateOptions(discovered);
  };

  // Track if initial data load has completed
  const [dataLoaded, setDataLoaded] = createSignal(false);

  const fetchTableData = async () => {
    // Cancel previous request if still pending
    if (tableDataController) {
      tableDataController.abort();
    }

    // Create new abort controller
    tableDataController = new AbortController();

    // Capture event type for this request so we don't apply a stale response over current type
    const requestEventType = (eventType() || 'TACK').trim() || 'TACK';

    if (requestEventType.toUpperCase() === 'TAKEOFF') {
      logDebug('FleetManeuversHistory: fetchTableData for TAKEOFF', { startDate: startDate(), endDate: endDate(), sourcesCount: sourcesStore.sources()?.length ?? 0 });
    }

    // Reset dataLoaded at the start of each fetch
    setDataLoaded(false);

    try {
      // Build filters object - use uppercase keys and array values
      const filters: Record<string, any[]> = {};
      
      // Parse grades (comma-separated, convert to integers)
      const grades = selectedGrades();
      if (grades.length > 0) {
        filters.GRADE = grades;
      }

      // Parse year / event / config (comma-separated string tokens — contains match on API)
      if (filterYear().trim()) {
        const years = splitMetadataFilterTokens(filterYear());
        if (years.length > 0) {
          filters.YEAR = years;
        }
      }

      if (filterEvent().trim()) {
        const events = splitMetadataFilterTokens(filterEvent());
        if (events.length > 0) {
          filters.EVENT = events;
        }
      }

      if (filterConfigValue().trim()) {
        const configs = splitMetadataFilterTokens(filterConfigValue());
        if (configs.length > 0) {
          filters.CONFIG = configs;
        }
      }

      appendCrewCodeToFilters(filters, filterCrew());

      // Foiling state is filtered client-side (see filterData) — not sent to the API — so toggling
      // states needs no refetch and every state stays present in the fetched top-N per state.

      // Training/Racing filter: pass to API so SQL can filter (racing = exclude training, training = only training)
      const trainingRacingVal = maneuverTrainingRacing();
      if (trainingRacingVal === 'TRAINING' || trainingRacingVal === 'RACING') {
        filters.TRAINING_RACING = [trainingRacingVal];
      }

      // Get date range from filterStore (for project mode) or use selectedDate
      const filterStoreStart = startDate();
      const filterStoreEnd = endDate();
      const date = selectedDate();
      
      let startDateValue: string;
      let endDateValue: string;
      
      if (filterStoreStart && filterStoreEnd) {
        startDateValue = filterStoreStart;
        endDateValue = filterStoreEnd;
      } else if (date) {
        startDateValue = date;
        endDateValue = date;
      } else {
        startDateValue = '2020-01-01';
        endDateValue = '2099-12-31';
      }

      // Get source names for the selected source IDs
      const selectedSourceIds = selectedSources();
      const sources = sourcesStore.sources();
      const selectedSourceNames: string[] = [];
      sources.forEach((source: any) => {
        if (selectedSourceIds.size === 0 || selectedSourceIds.has(source.source_id)) {
          selectedSourceNames.push(source.source_name);
        }
      });

      // Build URL with start_date, end_date, channels, source_names, and optional filters
      // For TAKEOFF, request only takeoff-relevant channels (not tack/gybe columns)
      const channelsToRequest = buildManeuversChannelsRequest(requestEventType, MANEUVERS_CHANNELS, {
        takeoffPrefix: ['twa_entry'],
      });
      let url = `${apiEndpoints.app.data}/maneuvers-history?class_name=${encodeURIComponent(selectedClassName())}&project_id=${encodeURIComponent(selectedProjectId())}&start_date=${encodeURIComponent(startDateValue)}&end_date=${encodeURIComponent(endDateValue)}&event_type=${encodeURIComponent(requestEventType)}&count=10&channels=${encodeURIComponent(JSON.stringify(channelsToRequest))}`;

      if (tws() === 'ALL') {
        url += `&tws_all=true`;
        url += `&color_by=${encodeURIComponent((color() || 'TWS').trim().toUpperCase())}`;
      }
      
      // Add source_names parameter
      if (selectedSourceNames.length > 0) {
        url += `&source_names=${encodeURIComponent(JSON.stringify(selectedSourceNames))}`;
      }
      
      // Add optional filters
      if (Object.keys(filters).length > 0) {
        url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
      }

      const result = await getData(url, tableDataController!.signal);

      // Ignore response if user changed maneuver type before this request completed (avoid stale overwrite)
      if ((eventType() || 'TACK').trim() !== requestEventType) {
        setDataLoaded(true);
        return;
      }

      if (result.success && result.data && Array.isArray(result.data)) {
        let allData: any[] = result.data;

        // Add datetimeLocal (dataset local time) for table and tooltips
        allData.forEach((item: any) => {
          const tz = (item.timezone ?? item.Timezone ?? "").trim() || undefined;
          const rawDt = item.Datetime ?? item.datetime;
          if (tz && rawDt != null && rawDt !== "") {
            try {
              const formatted = formatDateTime(rawDt, tz);
              if (formatted) item.datetimeLocal = formatted;
            } catch {
              // leave datetimeLocal undefined
            }
          }
        });

        // Log table data counts for debugging
        const uniqueEventIds = new Set(allData.map((item: any) => item.event_id));
        logDebug(`[FleetManeuversHistory] fetchTableData - API returned ${allData.length} records, ${uniqueEventIds.size} unique event_ids`);
        if (requestEventType.toUpperCase() === 'TAKEOFF') {
          logDebug('FleetManeuversHistory: TAKEOFF fetchTableData results', { totalRows: allData.length });
        }

        // TAKEOFF, BEARAWAY, ROUNDUP: Port/Stbd from entry or build TWA; otherwise by phase
        const evt = (requestEventType || '').toUpperCase();
        const usePortStbd = evt === 'TAKEOFF' || evt === 'BEARAWAY' || evt === 'ROUNDUP';
        if (usePortStbd) {
          allData.forEach((item: any) => {
            const twa = item.twa_entry ?? item.twa_build ?? item.Twa_start ?? 0;
            item.tack = Number(twa) > 0 ? 'STBD' : 'PORT';
          });
        } else if (phase() == 'FULL' || phase() == 'TURN') {
          allData.forEach((item: any) => {
            item.tack = item.twa_entry > 0 ? 'S - P' : 'P - S';
          });
        } else if (phase() == 'INVESTMENT') {
          allData.forEach((item: any) => {
            item.tack = item.twa_entry > 0 ? 'STBD' : 'PORT';
          });
        } else {
          allData.forEach((item: any) => {
            item.tack = item.twa_entry > 0 ? 'PORT' : 'STBD';
          });
        }

        // Generate TWS bins in intervals of 5 starting from the closest increment to the minimum
        // Extract all TWS values from data
        const twsValues: number[] = [];
        allData.forEach((item: any) => {
          const twsBin = item.tws_bin ?? item.tws_avg;
          if (twsBin !== null && twsBin !== undefined && !isNaN(Number(twsBin))) {
            twsValues.push(Number(twsBin));
          }
        });
        
        // Generate bins in increments of 5 starting from the minimum (no 'ALL' option for FleetManeuversHistory)
        let tws_bins: string[] = [];
        if (twsValues.length > 0) {
          const minTws = Math.min(...twsValues);
          const maxTws = Math.max(...twsValues);
          
          // Round down to nearest multiple of 5 for start
          const startBin = Math.floor(minTws / 5) * 5;
          // Round up to nearest multiple of 5 for end, then add one more increment
          const endBin = Math.ceil(maxTws / 5) * 5 + 5;
          
          // Generate bins in increments of 5
          for (let bin = startBin; bin <= endBin; bin += 5) {
            tws_bins.push(String(bin));
          }
        }
        
        setTwsOptions(['ALL', ...tws_bins]);
        
        const baseline = stateOptions();
        const { available, reconciled } = applyManeuverFoilingStateAfterFetch(
          allData,
          selectedStates(),
          hasSavedStateFilter,
          { availableStates: baseline.length > 0 ? baseline : undefined }
        );
        if (baseline.length === 0 && available.length > 0) {
          setStateOptions(available);
        }
        if (
          reconciled.length !== selectedStates().length ||
          reconciled.some((s, i) => s !== selectedStates()[i])
        ) {
          setSelectedStates(reconciled);
          setSelectedStatesManeuvers(reconciled);
        }
        
        // Select middle TWS option on load - FleetManeuversHistory always requires a TWS value
        if (tws_bins.length > 0) {
          const middleIndex = Math.floor(tws_bins.length / 2);
          const middleTws = tws_bins[middleIndex];
          const currentTws = String(tws() ?? "").trim();
          // Preserve 'ALL' as a valid selection; only reset when empty or an unknown bin
          if (!currentTws || (!tws_bins.includes(currentTws) && currentTws !== 'ALL')) {
            handleTws(middleTws);
          }
        }

        setManeuvers(allData)

        // Don't set filtered here - let filterData handle it to avoid race conditions
        // filterData will set filtered and triggerUpdate properly
        const eventIds = allData.map((item: any) => item.event_id);
        setSelection(eventIds as any)

        // Pass data directly to filterData to avoid race condition with store updates
        // filterData will set filtered() and triggerUpdate() at the end
        filterData(allData)
      } else {
        // Handle empty data - reset to empty state only if this response is still current
        if ((eventType() || 'TACK').trim() === requestEventType) {
          if (requestEventType.toUpperCase() === 'TAKEOFF') {
            logDebug('FleetManeuversHistory: TAKEOFF table/scatter received no rows from API');
          }
          setManeuvers([]);
          setTableData([]);
          setFiltered([]);
          setTwsOptions([]);
        }
      }
      setDataLoaded(true);
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Request was cancelled - this is expected, no error needed
      } else {
        if ((eventType() || 'TACK').trim() === requestEventType) {
          logError('Error fetching table data:', error as any);
          setManeuvers([]);
          setTableData([]);
          setFiltered([]);
          setTwsOptions([]);
        }
      }
      setDataLoaded(true);
    } finally {
      tableDataController = null;
    }
  }

  const handleEventType = (val: string): void => {
    const prev = normalizeManeuverEventType(eventType());
    const next = normalizeManeuverEventType(val);
    if (next === prev) return;
    setEventType(next);
    clearManeuverSelectionOnEventTypeChange('FleetManeuversHistory');
    // States are swapped by useManeuverStatesForEventType, which restores the incoming type's own
    // chips. The TAKEOFF special case that used to live here re-ran the whole settings load on the
    // way out, which also re-applied the saved grade / race / leg filters over whatever the user
    // had set — the state swap was never the only thing it did.
  };

  const handlePhase = (val: string): void => {
    setPhase(val)
    setTriggerUpdate(true);
  };

  const handleView = async (val: string): Promise<void> => {
    const normalized = (val === 'BOXES') ? 'SCATTER' : val;
    // Guard to satisfy the view signal's union type
    if (normalized === 'MAP' || normalized === 'TABLE' || normalized === 'SCATTER' || normalized === 'TIME SERIES') {
      setView(normalized);
      const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
      await logActivity(project_id, dataset_id, 'FleetManeuversHistory.tsx', 'Fleet Maneuvers History Report', `View changed to ${normalized}`);
    }
    setTriggerUpdate(true);
  };

  // Force SCATTER/BOXES content to remount and redraw when grouped toggles
  createEffect(() => {
    const g = grouped();
    if (view() === 'SCATTER') {
      setScatterKey((k: number) => k + 1);
    }
  });

  const handleColor = async (val: string): Promise<void> => {
    setColor(val);
    const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
    await logActivity(project_id, dataset_id, 'FleetManeuversHistory.tsx', 'Fleet Maneuvers History Report', `Color changed to ${val}`);
    if (tws() === 'ALL') {
      void fetchTableData();
    } else {
      setTriggerUpdate(true);
    }
  };

  const filterData = (dataOverride?: any[]): void => {
    // Use provided data or read from store - this avoids race conditions
    const maneuversData = dataOverride ?? maneuvers();
    if (!maneuversData || !Array.isArray(maneuversData) || maneuversData.length === 0) {
      setFiltered([]);
      setTableData([]);
      return;
    }

    let filteredData_tws: any[] = []

    if (tws() === 'ALL') {
      filteredData_tws = maneuversData;
    } else {
      const selectedTws = Number(tws());
      if (!isNaN(selectedTws)) {
        // Filter data where TWS value is within ±2.5 of the selected bin (intervals of 5)
        filteredData_tws = maneuversData.filter((item: any) => {
          const itemTws = item.tws_bin ?? item.tws_avg;
          if (itemTws === null || itemTws === undefined || isNaN(Number(itemTws))) {
            return false;
          }
          const itemTwsNum = Number(itemTws);
          // Filter within ±2.5 of the selected bin value
          // Bin 10 includes values from 7.5 to 12.5 (exclusive on upper bound to avoid overlap)
          const minTws = selectedTws - 2.5;
          const maxTws = selectedTws + 2.5;
          return itemTwsNum >= minTws && itemTwsNum < maxTws;
        });
      } else {
        // Fallback: if TWS is not a valid number, return empty array
        filteredData_tws = [];
      }
    }

    // Apply grade filter using selectedGrades array - use "greater than" logic
    // Use lowercase field names as primary with fallbacks for backward compatibility
    let filteredData: any[] = filteredData_tws;
    const grades = selectedGrades();
    if (grades.length > 0) {
      // For radio button behavior, only the first selected grade is used
      // Filter for grades greater than the selected grade
      const minGrade = Math.min(...grades.map(g => Number(g)).filter(g => !isNaN(g)));
      filteredData = filteredData_tws.filter((item: any) => {
        const itemGrade = item.grade ?? item.Grade ?? item.GRADE;
        if (itemGrade == null || itemGrade === undefined) return false;
        const numGrade = Number(itemGrade);
        if (isNaN(numGrade)) return false;
        return numGrade > minGrade;
      });
    }

    // Apply state filter using selectedStates array
    // Use lowercase field names as primary with fallbacks for backward compatibility
    const states = selectedStates();
    if (states.length > 0) {
      filteredData = filteredData.filter((item: any) => {
        const itemState = item.state ?? item.State ?? item.STATE;
        if (itemState == null || itemState === undefined) return false;
        const stateStr = String(itemState).trim();
        return states.some(selectedState => String(selectedState).trim().toLowerCase() === stateStr.toLowerCase());
      });
    }

    const races = selectedRaces();
    if (races.length > 0) {
      filteredData = filteredData.filter((item) => passesManeuverRaceFilter(item, races));
    }

    // Apply leg filter using selectedLegs array
    // Use lowercase field names as primary with fallbacks for backward compatibility
    const legs = selectedLegs();
    if (legs.length > 0) {
      filteredData = filteredData.filter((item: any) => {
        const legValue = item.leg_number ?? item.Leg_number ?? item.LEG;
        if (legValue == null || legValue === undefined) return false;
        return legs.includes(Number(legValue));
      });
    }

    const trainingRacing = maneuverTrainingRacing();
    if (trainingRacing) {
      filteredData = filteredData.filter((item) => passesManeuverTrainingRacingFilter(item, trainingRacing));
    }

    filteredData = applyManeuverEntrySide(filteredData, maneuverTackEntry()) as typeof filteredData;

    // If we have cut events, only show the cut maneuvers
    if (isCut() && cutEvents().length > 0) {
      const currentCutEvents = cutEvents();
      const cutIds = currentCutEvents
        .map((item) => {
          if (typeof item === 'number') {
            return Number(item);
          }
          if (item && typeof item === 'object' && 'event_id' in item) {
            const ev = (item as { event_id: unknown }).event_id;
            return typeof ev === 'number' ? ev : Number(ev);
          }
          return NaN;
        })
        .filter((id): id is number => Number.isFinite(id));

      if (cutIds.length > 0) {
        const cutIdSet = new Set(cutIds);
        filteredData = filteredData.filter((maneuver: any) => {
          const mid = Number(maneuver.event_id);
          return Number.isFinite(mid) && cutIdSet.has(mid);
        });
      } else {
        filteredData = [];
      }
    }

    const sortedRows = sortManeuverRows(filteredData, maneuverTableSort(), eventType());

    const eventIds = sortedRows.map((item: any) => item.event_id);
    const uniqueEventIds = new Set(eventIds);
    
    logDebug(`[FleetManeuversHistory] filterData - Final filtered data: ${filteredData.length} records, ${uniqueEventIds.size} unique event_ids`);
    logDebug(`[FleetManeuversHistory] filterData - Setting filtered() to ${eventIds.length} event_ids`);

    setFiltered(eventIds)
    setTableData(sortedRows)
    
    // Update selection to match filtered data (but don't override user's active selection)
    if (!hasSelection()) {
      setSelection(eventIds as any)
    }

    setTriggerUpdate(true)
  }

  const handleTws = async (val: string): Promise<void> => {
    const prevTws = tws();
    setTws(val);
    const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
    await logActivity(project_id, dataset_id, 'FleetManeuversHistory.tsx', 'Fleet Maneuvers History Report', `TWS changed to ${val}`);
    if (val === 'ALL' || prevTws === 'ALL') {
      void fetchTableData();
    } else {
      filterData();
    }
  };

  const handleLegendClick = (legendItem: string) => {
    // In grouped mode, legend toggles group selection (same key format and toggle as grouped DataTable)
    if (grouped()) {
      const key = legendTextToGroupKeyTable(legendItem, color() || 'TWS');
      setSelectedGroupKeys((prev) => {
        const next = toggleGroupKeyInList(prev, key);
        setHasSelection(next.length > 0);
        return next;
      });
      setTriggerSelection(true);
      return;
    }

    // Use full maneuvers data for selection - don't filter by cuts when selecting
    const dataSource = maneuvers();
    if (!dataSource || !Array.isArray(dataSource) || dataSource.length === 0) {
      logDebug('FleetManeuversHistory: Legend click on', legendItem, '- no maneuvers data available');
      return;
    }

    const currentColorField = color();
    const clickedItem = String(legendItem);
    
    // Filter maneuvers by current color field matching the clicked legend item
    const legendEntryIds = dataSource
      .filter((m: any) => {
        const modeUpper = String(currentColorField || 'SOURCE').toUpperCase();

        if (currentColorField === 'GRADE') {
          const gradeValue = m.grade ?? m.Grade ?? m.GRADE;
          const clickedStr = clickedItem;
          const gradeNum = Number(gradeValue);
          const clickedNum = Number(clickedStr);
          if (!isNaN(gradeNum) && !isNaN(clickedNum) && gradeNum === clickedNum) return true;
          if (String(gradeValue) === clickedStr) return true;
          return false;
        }

        if (currentColorField === 'LEG') {
          const legValue = m.leg_number ?? m.Leg_number ?? m.LEG;
          const clickedStr = clickedItem;
          const legNum = Number(legValue);
          const clickedNum = Number(clickedStr);
          if (!isNaN(legNum) && !isNaN(clickedNum) && legNum === clickedNum) return true;
          if (String(legValue) === clickedStr) return true;
          return false;
        }

        if (currentColorField === 'TACK') {
          const tackValue = String(m.tack || '').toUpperCase();
          const clickedTack = clickedItem.toUpperCase();
          if ((tackValue === 'PORT' || tackValue === 'S - P') && (clickedTack === 'PORT' || clickedTack === 'S - P')) {
            return true;
          }
          if ((tackValue === 'STBD' || tackValue === 'P - S') && (clickedTack === 'STBD' || clickedTack === 'P - S')) {
            return true;
          }
          return false;
        }

        if (['TWS', 'RACE', 'SOURCE', 'STATE', 'CONFIG', 'CREW', 'TESTS', 'YEAR', 'EVENT'].includes(modeUpper)) {
          const targetKey = legendTextToGroupKeyTable(clickedItem, modeUpper);
          return groupKeyEquals(getGroupKeyFromItem(m, modeUpper), targetKey);
        }

        const fieldValueStr = m[currentColorField.toLowerCase()] ?? m[currentColorField];
        if (fieldValueStr === undefined || fieldValueStr === null) return false;
        if (String(fieldValueStr) === clickedItem) return true;
        const fieldNum = Number(fieldValueStr);
        const clickedNum = Number(clickedItem);
        if (!isNaN(fieldNum) && !isNaN(clickedNum) && fieldNum === clickedNum) return true;
        if (String(fieldValueStr).toLowerCase() === clickedItem.toLowerCase()) return true;
        return false;
      })
      .map((m: any) => m.event_id)
      .filter((id): id is number => id !== undefined && id !== null && !isNaN(Number(id)));

    if (legendEntryIds.length === 0) {
      logDebug('FleetManeuversHistory: Legend click on', legendItem, '- no matching event IDs found');
      return;
    }

    // Get current selection
    const currentSelected = selectedEvents();
    const currentSelectedSet = new Set(currentSelected);
    
    // Check if all legend entry IDs are already selected
    const allSelected = legendEntryIds.every(id => currentSelectedSet.has(id));
    
    if (allSelected) {
      // All IDs are selected → remove all legend entry IDs
      const updatedSelection = currentSelected.filter(id => !legendEntryIds.includes(id));
      if (updatedSelection.length > 0) {
        setLegendSelectionActive(true);
      }
      setSelectedEvents(updatedSelection);
      setHasSelection(updatedSelection.length > 0);
      logDebug('FleetManeuversHistory: Legend click on', legendItem, '- deselected all', legendEntryIds.length, 'event IDs');
    } else {
      // Not all IDs are selected → select all legend entry IDs (merge with existing)
      const updatedSelection = Array.from(new Set([...currentSelected, ...legendEntryIds]));
      setLegendSelectionActive(true);
      setSelectedEvents(updatedSelection);
      setHasSelection(updatedSelection.length > 0);
      logDebug('FleetManeuversHistory: Legend click on', legendItem, '- selected all', legendEntryIds.length, 'event IDs');
    }
    
    // Refresh selection styling without refetching timeseries data
    setTriggerSelection(true);
  };

  // React to eventType / date range / source selection — refresh state options and refetch table data
  const [historyMountComplete, setHistoryMountComplete] = createSignal(false);
  createEffect(
    on(
      () => {
        const sources = sourcesStore.sources() ?? [];
        const allIds = sources
          .map((s: { source_id?: number }) => s.source_id)
          .filter((id: unknown): id is number => typeof id === 'number' && id > 0)
          .sort((a, b) => a - b);
        const sel = selectedSources();
        const isAll =
          sel.size === 0 ||
          (allIds.length > 0 &&
            sel.size === allIds.length &&
            allIds.every((id) => sel.has(id)));
        const selKey = isAll ? '__ALL__' : [...sel].sort((a, b) => a - b).join(',');

        return {
          mount: historyMountComplete(),
          et: normalizeManeuverEventType(eventType()),
          start: startDate() || '',
          end: endDate() || '',
          ready: sourcesStore.isReady(),
          nsrc: sources.length,
          selKey,
        };
      },
      (cur, prev) => {
        if (!cur.mount) return;
        if (!cur.ready || cur.nsrc === 0 || !cur.et || !cur.start || !cur.end || cur.selKey === '') return;

        const payload = { et: cur.et, start: cur.start, end: cur.end, selKey: cur.selKey };
        const prevPayload =
          prev && prev.mount ? { et: prev.et, start: prev.start, end: prev.end, selKey: prev.selKey } : null;
        if (prevPayload && JSON.stringify(payload) === JSON.stringify(prevPayload)) return;

        void refreshManeuverStateOptions();
        void untrack(() => fetchTableData());
      },
      { defer: true }
    )
  );

  // When startDate is set but endDate is missing, fetch last_date and set endDate (or fallback to startDate)
  const ensureEndDateFilled = async (): Promise<void> => {
    if (endDate()) return;
    const start = startDate();
    if (!start) return;
    try {
      const { selectedClassName, selectedProjectId } = persistantStore;
      const className = selectedClassName();
      const projectId = selectedProjectId();
      if (!className || !projectId) {
        setEndDate(start);
        return;
      }
      const controller = new AbortController();
      const result = await getData(
        `${apiEndpoints.app.datasets}/last_date?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&source_id=0`,
        controller.signal
      );
      if (result?.success && result?.data) {
        const d = new Date(result.data);
        if (!isNaN(d.getTime())) {
          const formatted = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          setEndDate(formatted);
          logDebug('FleetManeuversHistory: Filled end date from last_date', formatted);
          return;
        }
      }
      setEndDate(start);
      logDebug('FleetManeuversHistory: Filled end date with start date (fallback)');
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setEndDate(start);
      logDebug('FleetManeuversHistory: Filled end date with start date (error fallback)');
    }
  };

  // Initialize default date range (1 year back from latest data, source_id=0 for fleet)
  const initializeDefaultDateRange = async (): Promise<void> => {
    try {
      const { selectedClassName, selectedProjectId } = persistantStore;
      const className = selectedClassName();
      const projectId = selectedProjectId();

      if (!className || !projectId) {
        logDebug('FleetManeuversHistory: Cannot initialize default date range - missing required values');
        return;
      }

      // For fleet pages, use source_id=0
      const controller = new AbortController();
      const result = await getData(
        `${apiEndpoints.app.datasets}/last_date?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&source_id=0`,
        controller.signal
      );

      if (result.success && result.data) {
        const dateStr = String(result.data).trim();
        const endDateValue = new Date(dateStr);
        if (isNaN(endDateValue.getTime())) {
          logDebug('FleetManeuversHistory: last_date invalid, skipping default date range');
          return;
        }
        const startDateValue = new Date(endDateValue.getTime());
        // Set start date to 1 year (365 days) before end date
        startDateValue.setDate(startDateValue.getDate() - 365);

        // Format as YYYY-MM-DD
        const formatDate = (date: Date): string => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        const formattedEndDate = formatDate(endDateValue);
        const formattedStartDate = formatDate(startDateValue);
        // Only set if both look valid (YYYY-MM-DD)
        const validFormat = /^\d{4}-\d{2}-\d{2}$/;
        if (!validFormat.test(formattedEndDate) || !validFormat.test(formattedStartDate)) {
          logDebug('FleetManeuversHistory: Formatted dates invalid, skipping', { formattedStartDate, formattedEndDate });
          return;
        }

        // Set end date first so it is never left empty if something fails after
        setEndDate(formattedEndDate);
        setStartDate(formattedStartDate);

        // Save to persistent settings
        const currentUser = user();
        if (currentUser?.user_id) {
          await persistentSettingsService.saveSettings(
            currentUser.user_id,
            className,
            projectId,
            {
              performanceHistoryDateRange: {
                startDate: formattedStartDate,
                endDate: formattedEndDate
              }
            }
          );
          logDebug('FleetManeuversHistory: Initialized and saved default date range (1 year)', {
            startDate: formattedStartDate,
            endDate: formattedEndDate
          });
        }
      } else {
        logDebug('FleetManeuversHistory: Failed to fetch last_date for default date range');
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      logError('FleetManeuversHistory: Error initializing default date range:', err);
    }
  };

  // Whenever startDate is set but endDate is missing, fill endDate (so UI never shows empty end date)
  createEffect(() => {
    const start = startDate();
    const end = endDate();
    if (start && !end) {
      ensureEndDateFilled();
    }
  });

  // Initialize date range and selectedSources from persistent settings
  createEffect(async () => {
    const sourcesReady = sourcesStore.isReady();
    const sources = sourcesStore.sources();
    const currentUser = user();
    
    if (!sourcesReady || !sources || sources.length === 0 || !currentUser?.user_id) {
      return;
    }
    
    const { selectedClassName, selectedProjectId } = persistantStore;
    const className = selectedClassName();
    const projectId = selectedProjectId();
    if (!className || !projectId) {
      return;
    }

    try {
      const settings = await persistentSettingsService.loadSettings(
        currentUser.user_id,
        className,
        projectId
      );

      // Always ensure date range is populated when missing (so End Date is never left empty)
      const needDateRange = !startDate() || !endDate();
      if (needDateRange) {
        let hasDateRange = false;
        if (settings?.performanceHistoryDateRange) {
          const dateRange = settings.performanceHistoryDateRange;
          if (dateRange.startDate && dateRange.endDate) {
            setStartDate(dateRange.startDate);
            setEndDate(dateRange.endDate);
            hasDateRange = true;
            logDebug('FleetManeuversHistory: Loaded date range from persistent settings', {
              startDate: dateRange.startDate,
              endDate: dateRange.endDate
            });
          }
        }
        if (!hasDateRange) {
          await initializeDefaultDateRange();
        }
        // Ensure end date is never left empty (e.g. if start was set but end was not)
        if (!endDate() && startDate()) {
          await ensureEndDateFilled();
        }
      }

      // Only initialize sources when not already set (avoid overwriting user selection)
      if (selectedSources().size === 0) {
        if (settings?.fleetPerformanceSources && Array.isArray(settings.fleetPerformanceSources) && settings.fleetPerformanceSources.length > 0) {
          // Convert source names to source IDs
          const sourceIds = new Set<number>();
          settings.fleetPerformanceSources.forEach((sourceName: string) => {
            const source = sources.find((s: any) => s.source_name === sourceName);
            if (source && source.source_id) {
              sourceIds.add(source.source_id);
            }
          });
          if (sourceIds.size > 0) {
            setSelectedSources(sourceIds);
            logDebug('FleetManeuversHistory: Initialized sources from persistent settings', Array.from(sourceIds));
          } else {
            const allSourceIds = new Set(sources.map((s: any) => s.source_id).filter((id: any) => id !== null && id !== undefined));
            setSelectedSources(allSourceIds);
            logDebug('FleetManeuversHistory: No matching sources found, defaulting to all sources');
          }
        } else {
          const allSourceIds = new Set(sources.map((s: any) => s.source_id).filter((id: any) => id !== null && id !== undefined));
          setSelectedSources(allSourceIds);
          logDebug('FleetManeuversHistory: No saved source settings, defaulting to all sources');
        }
      }
    } catch (error) {
      logDebug('FleetManeuversHistory: Error loading source settings, defaulting to all sources', error);
      if (selectedSources().size === 0) {
        const allSourceIds = new Set(sources.map((s: any) => s.source_id).filter((id: any) => id !== null && id !== undefined));
        setSelectedSources(allSourceIds);
      }
    }
  });

  // Sync selectedSources to filterStore (for Map component to use)
  createEffect(() => {
    const selected = selectedSources();
    const sources = sourcesStore.sources();
    
    if (selected.size > 0 && sources && sources.length > 0) {
      // Convert source IDs to source names for filterStore
      const sourceNames: string[] = [];
      sources.forEach((source: any) => {
        if (selected.has(source.source_id)) {
          sourceNames.push(source.source_name);
        }
      });
      
      // Sync to filterStore so Map component can use SOURCE_NAME filter
      if (sourceNames.length > 0) {
        setFilterStoreSelectedSources(sourceNames);
        logDebug('FleetManeuversHistory: Synced selectedSources to filterStore', sourceNames);
      }
    }
  });

  // React to client-side filter changes — refilter when grade/state/tack change
  createEffect(on([selectedGrades, selectedStates, maneuverTackEntry], () => {
    if (untrack(() => maneuvers().length > 0)) {
      filterData();
    }
  }));

  // React to cut events - refilter when cut state or cut payload changes
  createEffect(on([isCut, cutEvents], () => {
    if (untrack(() => maneuvers().length > 0)) {
      filterData();
    }
  }));

  // React to tack entry filter — client-side only, no refetch
  createEffect(on(maneuverTackEntry, () => {
    if (untrack(() => maneuvers().length > 0)) {
      filterData();
    }
  }));

  // React to filter changes - refetch when grades or training/racing change (API-based filtering).
  // Use on() so we only track these deps; fetchTableData inside untrack to avoid tracking its internal reads (prevents loop).
  let lastFilterSnapshot = '';
  createEffect(
    on(
      [selectedGrades, maneuverTrainingRacing],
      async ([grades, trainingRacing]) => {
        const gradesKey = JSON.stringify([...(grades || [])].sort());
        const trKey = trainingRacing ?? '';
        const snapshot = gradesKey + '|' + trKey;
        if (snapshot === lastFilterSnapshot) return;
        lastFilterSnapshot = snapshot;
        if (untrack(() => dataLoaded())) {
          await untrack(() => fetchTableData());
        }
      },
      { defer: true }
    )
  );

  // React to view changes - update container heights when view changes
  createEffect(() => {
    // Access view() to trigger effect
    view();
    showTimeline();
    filtered().length;
    // Colour mode changes the legend's wrapped height, so re-measure on it too.
    color();
    maneuverColorLegendItems().length;
    // Use setTimeout to ensure DOM has updated. Delegate to updateContainerHeights rather than
    // repeating its arithmetic: this effect used to carry a second copy, so anything added to the
    // reserve there (e.g. the color legend row) was silently overwritten here a tick later.
    setTimeout(() => {
      updateContainerHeights();
    }, 0);
  });

  createEffect(() => {
    const eventList = selectedEvents();
    if (eventList.length === 0) return;

    const datasetId = selectedDatasetId();
    if (!datasetId || datasetId <= 0) {
      logDebug('FleetManeuversHistory: Skipping times fetch in fleet context (endpoint requires dataset_id)');
      return;
    }

    if (timesController) {
      timesController.abort();
    }

    timesController = new AbortController();
    const signal = timesController.signal;
    const className = selectedClassName();
    const projectId = selectedProjectId();

    void runEventTimesSelectionSync({
      fetchTimes: () =>
        getEventTimes(
          {
            class_name: className,
            project_id: projectId,
            dataset_id: datasetId,
            event_list: eventList,
          },
          signal
        ),
      applySelection: (result) => {
        setSelection((result as { data?: unknown }).data);
      },
    }).finally(() => {
      timesController = null;
    });
  });

  // Populate selectedRanges from maneuver event times + table rows (multi-dataset fleet; selectionStore skips getEventTimeRanges on this page).
  let lastFleetHistoryRangesSig = "";
  createEffect(() => {
    const ids = selectedEvents();
    const selectionRows = selection();
    if (!Array.isArray(ids) || ids.length === 0) {
      lastFleetHistoryRangesSig = "";
      // Clear explicitly; the store's follow-up skips its clear while a brush is active.
      if (untrack(() => selectedRanges()).length > 0) {
        setSelectedRanges([]);
      }
      return;
    }
    const table = tabledata();
    const lookupRows = (Array.isArray(table) ? table : []) as Record<string, unknown>[];
    const selectionTimeRows = Array.isArray(selectionRows)
      ? selectionRows.filter(
          (row) => row != null && typeof row === "object" && !Array.isArray(row) && "start_time" in row
        )
      : [];
    if (lookupRows.length === 0 && selectionTimeRows.length === 0) return;

    // Fleet overlays stay boat-colored: returning undefined lets the map fall back to the source/group color.
    const sourceColorScale = fleetManeuverSourceColorScale();
    const resolveColor: SelectionRangeColorResolver = (row) => {
      const name = extractRowSourceName(row);
      if (sourceColorScale && name) {
        return String(sourceColorScale(name.toLowerCase()));
      }
      return undefined;
    };

    // Was a hand-rolled loop over table rows only: it ignored the getEventTimes rows and emitted zero-span
    // ranges (end_time === start_time), which can never hold the >= 2 points a track overlay needs.
    const ranges = buildSelectedRangesForEvents({
      eventIds: ids,
      selectionData: selectionTimeRows,
      lookupRows,
      resolveColor,
      sources: sourcesStore.sources() ?? [],
    });
    if (ranges.length === 0) return;

    const sig = selectionRangesSignatureForEvents(ids, ranges);
    if (sig === lastFleetHistoryRangesSig) return;
    lastFleetHistoryRangesSig = sig;
    setSelectedRanges(ranges);
  });

  // Function to sync table height with map SVG height
  const syncTableHeightWithMap = (): void => {
    if (view() !== 'MAP') return;
    
    const mapElement = document.querySelector('#maneuver-map') as HTMLElement | null;
    const tableArea = document.getElementById('table-area') as HTMLElement | null;
    
    if (mapElement && tableArea) {
      // Get the actual height of the map element (including SVG)
      const mapHeight = mapElement.offsetHeight || mapElement.clientHeight;
      
      // Set table area to match map height
      if (mapHeight > 0) {
        tableArea.style.height = `${mapHeight}px`;
        tableArea.style.minHeight = `${mapHeight}px`;
      }
    }
  };

  // Function to update container heights based on actual viewport (full screen)
  const updateContainerHeights = (): void => {
    // Get the actual available height from the viewport (full screen)
    // Header height is 58px (50px + 8px padding)
    const headerHeight = 58;
    const timelineOn = showTimeline() && filtered().length > 0;

    // For MAP view: fleet history used aggressive negative spacing; reserve extra when selection timeline is on
    // Measured, not a constant: the legend row wraps to a different height per colour mode.
    const legendReservePx = maneuverColorLegendReservePx();

    const additionalSpacingForMap = (timelineOn ? 218 : -50) + legendReservePx;
    const mapHeight = window.innerHeight - headerHeight - additionalSpacingForMap;

    // For TABLE view: account for header, padding, and button area
    const additionalSpacingForTable = (timelineOn ? 248 : 128) + legendReservePx;
    const tableHeight = window.innerHeight - headerHeight - additionalSpacingForTable;
    
    // Update heights dynamically for all views
    const mapLayout = document.querySelector('.maneuver-map-layout') as HTMLElement | null;
    const tableWrapper = document.getElementById('datatable-big-wrapper') as HTMLElement | null;
    
    if (mapLayout) {
      mapLayout.style.height = `${mapHeight}px`;
      mapLayout.style.minHeight = `${mapHeight}px`;
      mapLayout.style.maxHeight = `${mapHeight}px`;
    }
    
    if (tableWrapper) {
      tableWrapper.style.height = `${tableHeight}px`;
      tableWrapper.style.maxHeight = `${tableHeight}px`;
      tableWrapper.style.overflow = 'hidden'; // Ensure wrapper contains scrollable content
    }
    
    // Sync table height with map SVG height for MAP view
    setTimeout(() => {
      syncTableHeightWithMap();
    }, 100); // Small delay to ensure map is rendered
  };
  
  // Function to observe sidebar for collapse/expand changes
  const observeSidebar = (): MutationObserver | null => {
    const sidebar = document.querySelector('.sidebar');
    if (!sidebar) return null;
    
    const observer = new MutationObserver(() => {
      // When sidebar class changes (collapsed/expanded), update heights
      updateContainerHeights();
    });
    
    observer.observe(sidebar, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return observer;
  };

  // Load filters from persistent settings
  const loadFiltersFromPersistentSettings = async () => {
    const currentUser = user();
    if (currentUser?.user_id) {
      try {
        const { selectedClassName, selectedProjectId } = persistantStore;
        const className = selectedClassName();
        const projectId = selectedProjectId();

        if (className && projectId) {
          const settings = await persistentSettingsService.loadSettings(
            currentUser.user_id,
            className,
            projectId
          );

          logDebug('FleetManeuversHistory: Loaded settings from persistent settings', settings);
          let gradesLoaded = false;
          if (settings?.maneuverFilters) {
            const filters = settings.maneuverFilters;
            if (filters.grades && Array.isArray(filters.grades) && filters.grades.length > 0) {
              setSelectedGrades(filters.grades);
              setSelectedGradesManeuvers(filters.grades.map((g: number) => String(g)));
              gradesLoaded = true;
              logDebug('FleetManeuversHistory: Loaded grade filters from persistent settings', filters.grades);
            }
            // filters.states is deliberately NOT read here. States are per maneuver type now
            // (useManeuverStatesForEventType), and this flat value is only the first-visit seed that
            // hook falls back to.
            if (filters.races && Array.isArray(filters.races) && filters.races.length > 0) {
              setSelectedRaces(filters.races);
              setSelectedRacesManeuvers(filters.races.map((r: number | string) => String(r)));
              logDebug('FleetManeuversHistory: Loaded race filters from persistent settings', filters.races);
            }
            if (filters.legs && Array.isArray(filters.legs) && filters.legs.length > 0) {
              setSelectedLegs(filters.legs);
              setSelectedLegsManeuvers(filters.legs.map((l: number) => String(l)));
              logDebug('FleetManeuversHistory: Loaded leg filters from persistent settings', filters.legs);
            }
            if (filters.trainingRacing === 'TRAINING' || filters.trainingRacing === 'RACING') {
              setManeuverTrainingRacing(filters.trainingRacing);
              logDebug('FleetManeuversHistory: Loaded trainingRacing filter from persistent settings', filters.trainingRacing);
            }
            if (filters.tackEntry === 'PORT' || filters.tackEntry === 'STBD') {
              setManeuverTackEntry(filters.tackEntry);
              logDebug('FleetManeuversHistory: Loaded tack entry filter from persistent settings', filters.tackEntry);
            }
          } else {
            logDebug('FleetManeuversHistory: No maneuverFilters found in settings', settings);
          }
          
          // Set default grade >1 (shows grades 2 and 3) if no grades were loaded from persistent settings
          if (!gradesLoaded) {
            setSelectedGrades([1]);
            setSelectedGradesManeuvers(['1']);
            logDebug('FleetManeuversHistory: Set default grade filter to [1] (>1)');
          }
        }
      } catch (error) {
        logDebug('FleetManeuversHistory: Error loading filters from persistent settings:', error);
        if (selectedGrades().length === 0) {
          setSelectedGrades([1]);
          setSelectedGradesManeuvers(['1']);
          logDebug('FleetManeuversHistory: Set default grade filter to [1] (>1) after error');
        }
      }
    } else {
      if (selectedGrades().length === 0) {
        setSelectedGrades([1]);
        setSelectedGradesManeuvers(['1']);
        logDebug('FleetManeuversHistory: Set default grade filter to [1] (>1) (no user)');
      }
    }
  };

  onMount(async () => {
    // Teardown holders + onCleanup are registered synchronously, BEFORE the awaits below. Solid
    // only has an owner synchronously, so an onCleanup placed next to the resources (created after
    // those awaits) silently never runs — and the disposer this function used to `return` was
    // discarded outright, because Solid ignores onMount return values. The result was one leaked
    // scaling teardown, two ResizeObservers, a MutationObserver, a 100 ms interval and two window
    // listeners per mount.
    let cleanupScaling: (() => void) | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let mapObserver: ResizeObserver | null = null;
    let sidebarObserver: MutationObserver | null = null;
    let sidebarCheckInterval: ReturnType<typeof setInterval> | null = null;
    let handleResize: (() => void) | null = null;
    let handleKeyDown: ((event: KeyboardEvent) => void) | null = null;
    let handleBaselineKeyDown: ((event: KeyboardEvent) => void) | null = null;

    onCleanup(() => {
      cleanupScaling?.();
      resizeObserver?.disconnect();
      mapObserver?.disconnect();
      sidebarObserver?.disconnect();
      if (sidebarCheckInterval) clearInterval(sidebarCheckInterval);
      if (handleResize) window.removeEventListener('resize', handleResize);
      if (handleKeyDown) window.removeEventListener('keydown', handleKeyDown);
      if (handleBaselineKeyDown) window.removeEventListener('keydown', handleBaselineKeyDown);
    });
    resetManeuverTableSort();
    const restored = willRestoreShare(FLEET_MANEUVERS_HISTORY_PAGE_KEY)
      ? await shareRestoreReady(FLEET_MANEUVERS_HISTORY_PAGE_KEY)
      : false;
    const sharedBaseline = consumeShareSpecifiedBaseline(FLEET_MANEUVERS_HISTORY_PAGE_KEY);
    if (!restored || !sharedBaseline) {
      // Before the first chart draw, so a remembered baseline is on the initial render.
      await baselineDisplay.restoreSavedBaseline();
    }
    await logPageLoad('FleetManeuversHistory.tsx', 'Fleet Maneuvers Analysis Report');
    
    // Wait for sources to be ready (poll until ready)
    while (!sourcesStore.isReady()) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    if (!restored) {
      // Set default color to SOURCE for fleet context
      setColor('SOURCE');
      setEventType("TACK")
      setView("MAP")
    }

    // Restrict grade options to [1, 2] only (exclude 0) for history pages
    // Grade 1 means >1 (shows grades 2 and 3), Grade 2 means >2 (shows only grade 3)
    setGradeOptions([1, 2]);

    if (!restored) {
      const className = selectedClassName();
      const projectId = selectedProjectId();
      const projectFilters = (className && projectId) ? await getProjectManeuverFilters(className, projectId, '1970-01-01') : null;
      if (projectFilters === null) {
        await loadFiltersFromPersistentSettings();
      }

      // Set in-report defaults first (when none are set)
      if (!selectedGrades().length) {
        setSelectedGrades([1]);
        setSelectedGradesManeuvers(['1']);
      }

      // Apply project filters on top (overrides defaults; empty array means "no filter")
      if (projectFilters !== null) {
        if (projectFilters.grades !== undefined) {
          const grades = Array.isArray(projectFilters.grades) ? projectFilters.grades : [];
          setSelectedGrades(grades);
          setSelectedGradesManeuvers(grades.map(g => String(g)));
          logDebug('FleetManeuversHistory: Applied grade filters from project default', projectFilters.grades);
        }
        if (projectFilters.states !== undefined) {
          // A first-visit seed, not an override: the project holds one flat list for every maneuver
          // type, so it yields to this user's own per-type chips. See applyProjectDefaultStates.
          await applyProjectDefaultStates(projectFilters.states);
          logDebug('FleetManeuversHistory: Offered state filters from project default', projectFilters.states);
        }
      }

      // Default year/event from dataset selection when not ALL (leave empty if ALL)
      const dsYear = selectedYear();
      const dsEvent = selectedEvent();
      if ((!filterYear() || filterYear().trim() === '') && dsYear != null && String(dsYear).trim() !== '' && String(dsYear).trim().toUpperCase() !== 'ALL') {
        setFilterYear(String(dsYear).trim());
      }
      if ((!filterEvent() || filterEvent().trim() === '') && dsEvent != null && String(dsEvent).trim() !== '' && String(dsEvent).trim().toUpperCase() !== 'ALL') {
        setFilterEvent(String(dsEvent).trim());
      }
    }
    
    // Ensure date range is set (1 year if not already set from settings)
    if (!startDate() || !endDate()) {
      await initializeDefaultDateRange();
    }

    await refreshManeuverStateOptions();

    // Allow the unified createEffect to run; avoids overlapping fetches during async onMount
    setHistoryMountComplete(true);
    
    // Set up dynamic scaling for media-container using the global utility
    cleanupScaling = setupMediaContainerScaling({
      logPrefix: 'FleetManeuvers'
    });
    
    // Set initial heights
    updateContainerHeights();
    
    // Update heights on container resize (scaling is handled by setupMediaContainerScaling)
    resizeObserver = new ResizeObserver(() => {
      updateContainerHeights();
    });
    
    // Observe map element to sync table height when map resizes
    mapObserver = new ResizeObserver(() => {
      if (view() === 'MAP') {
        syncTableHeightWithMap();
      }
    });
    
    const mediaContainer = document.getElementById('media-container');
    if (mediaContainer) {
      // Observe the appropriate reference container (split panel or parent)
      const splitPanel = mediaContainer.closest('.split-panel');
      const mainContent = mediaContainer.closest('#main-content');
      const reference = splitPanel || mainContent || mediaContainer.parentElement;
      if (reference) {
        resizeObserver.observe(reference);
      }
    }
    
    // Observe map element for height changes
    const mapElement = document.querySelector('#maneuver-map');
    if (mapElement) {
      mapObserver.observe(mapElement);
    }
    
    // Also observe map area container
    const mapArea = document.getElementById('map-area');
    if (mapArea) {
      mapObserver.observe(mapArea);
    }
    
    // Observe sidebar for collapse/expand changes
    sidebarObserver = observeSidebar();
    
    // Poll for sidebar changes (since same-tab localStorage changes don't trigger storage event)
    sidebarCheckInterval = setInterval(() => {
      const sidebar = document.querySelector('.sidebar');
      if (sidebar) {
        const isCollapsed = sidebar.classList.contains('collapsed');
        const wasCollapsed = localStorage.getItem('_sidebarWasCollapsed') === 'true';
        if (isCollapsed !== wasCollapsed) {
          localStorage.setItem('_sidebarWasCollapsed', isCollapsed.toString());
          updateContainerHeights();
        }
      }
    }, 100);
    
    // Also listen to window resize for when the main window changes size
    handleResize = (): void => {
      updateContainerHeights();
    };
    window.addEventListener('resize', handleResize);

    // 'h' key: Hide selected events; 'x' clear; 'c' cut
    handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      if (document.querySelector('.pagesettings-modal')) return;

      if (event.key === 'x' || event.key === 'X') {
        event.preventDefault();
        logDebug('FleetManeuversHistory: Clearing selection (x key pressed)');
        clearSelection();
        requestAnimationFrame(() => setTriggerUpdate(true));
        return;
      }

      if (event.key === 'c' || event.key === 'C') {
        event.preventDefault();
        const currentSelected = selectedEvents();
        if (currentSelected && currentSelected.length > 0) {
          logDebug('FleetManeuversHistory: Cutting selection (c key pressed)');
          cutSelection();
          requestAnimationFrame(() => setTriggerUpdate(true));
        }
        return;
      }

      if (event.key === 'h' || event.key === 'H') {
        const currentSelected = selectedEvents();
        if (currentSelected && currentSelected.length > 0) {
          event.preventDefault();
          hideSelectedEvents();
          requestAnimationFrame(() => setTriggerUpdate(true));
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    // '+' opens Save to Baseline. Its own listener, unaffected by handleKeyDown's early-returns;
    // the hook does its own input/modal checks (mirrors PerformanceHistory).
    handleBaselineKeyDown = (event: KeyboardEvent) => {
      baselineSave.handleSaveHotkey(event);
    };
    window.addEventListener('keydown', handleBaselineKeyDown);

    // Teardown is registered by the onCleanup at the top of this onMount (see the note there).
  });

  // Cleanup abort controllers and other resources on component unmount
  onCleanup(() => {
    // Abort any pending table data requests
    if (tableDataController) {
      tableDataController.abort();
      tableDataController = null;
    }
    
    // Abort any pending times requests
    if (timesController) {
      timesController.abort();
      timesController = null;
    }
    
    // Reset data loaded state
    setDataLoaded(false);
    
    // Clear any stored data to free memory
    setManeuvers([]);
    setTableData([]);
    setFiltered([]);
    
    logDebug('FleetManeuversHistory: Cleanup complete - abort controllers cleared and data reset');
  });

  const toggleRaceFilter = (race: number | string) => {
    const current = selectedRaces();
    const next = current.includes(race) ? current.filter(r => r !== race) : [...current, race];
    setSelectedRaces(next);
    setSelectedRacesManeuvers(next.map(r => String(r)));
  };

  const toggleLegFilter = (leg: number) => {
    const current = selectedLegs();
    const next = current.includes(leg) ? current.filter(l => l !== leg) : [...current, leg];
    setSelectedLegs(next);
    setSelectedLegsManeuvers(next.map(l => String(l)));
  };

  const toggleGradeFilter = (grade: number) => {
    const current = selectedGrades();
    const next = current.includes(grade) ? [] : [grade];
    setSelectedGrades(next);
    setSelectedGradesManeuvers(next.map(g => String(g)));
  };

  const toggleStateFilter = (state: string) => {
    const current = selectedStates();
    const next = current.includes(state) ? current.filter(s => s !== state) : [...current, state];
    setSelectedStates(next);
    setSelectedStatesManeuvers(next);
    // Remembered against the maneuver type on screen, so a type switch brings back its own chips.
    persistStates();
  };

  createEffect(() => {
    const rLocal = raceOptions().map((x) => String(x));
    const lLocal = legOptions().map((x) => String(x));
    const gLocal = gradeOptions().map((x) => String(x));
    const rs = storeRaceOptions().map((x) => String(x));
    const ls = storeLegOptions().map((x) => String(x));
    const gs = storeGradeOptions().map((x) => String(x));
    if (JSON.stringify([...rLocal].sort()) !== JSON.stringify([...rs].sort())) {
      pushRaceOptionsToFilterStore(rLocal);
    }
    if (JSON.stringify([...lLocal].sort()) !== JSON.stringify([...ls].sort())) {
      pushLegOptionsToFilterStore(lLocal);
    }
    if (JSON.stringify([...gLocal].sort()) !== JSON.stringify([...gs].sort())) {
      pushGradeOptionsToFilterStore(gLocal);
    }
  });

  // Create dataSourcesOptions from sourcesStore
  const dataSourcesOptions = createMemo(() => {
    return sourcesStore.sources().map(source => ({
      key: `source-${source.source_id}`,
      label: source.source_name,
      signal: [
        () => selectedSources().has(source.source_id),
        (value: boolean) => {
          const current = selectedSources();
          const next = new Set(current);
          if (value) {
            next.add(source.source_id);
          } else {
            next.delete(source.source_id);
          }
          setSelectedSources(next);
        }
      ] as [() => boolean, (value: boolean) => void]
    }));
  });

  // Check if maneuvers exist
  const hasManeuvers = createMemo(() => {
    const maneuversData = maneuvers();
    return maneuversData && Array.isArray(maneuversData) && maneuversData.length > 0;
  });

  // Check if filtered maneuvers exist (after grade/TWS filtering)
  const hasFilteredManeuvers = createMemo(() => {
    const filteredIds = filtered();
    return filteredIds && Array.isArray(filteredIds) && filteredIds.length > 0;
  });

  const maneuverTimelinePoints = createMemo(() => {
    const rows = tabledata();
    if (!rows?.length) return [] as Record<string, unknown>[];
    return mapManeuverRowsToScatterTimeseriesPoints(rows as Record<string, unknown>[]);
  });

  const maneuverTimelineGroups = createMemo(() => {
    const rows = tabledata();
    return maneuverTimelineLegendGroups(rows as Record<string, unknown>[], color());
  });

  // Expensive pass: grouping + colours. Re-runs on data or colour-mode change only.
  const maneuverColorLegendBase = createMemo(() => {
    const rows = tabledata() as Record<string, unknown>[];
    // Touch the sources store so SOURCE colours repaint once the fleet colours load.
    if (String(color() || '').toUpperCase() === 'SOURCE') {
      sourcesStore.sources();
      sourcesStore.isReady();
    }
    return buildManeuverColorLegendItems(rows, color(), {
      eventType: eventType(),
      baseline: { name: maneuverBaselineLabel(), color: maneuverBaselineStore.baselineColor() },
    });
  });

  // Cheap pass: pill rings track the same selection the views highlight.
  const maneuverColorLegendItems = createMemo(() =>
    applyManeuverLegendSelection(maneuverColorLegendBase(), color(), {
      grouped: grouped(),
      selectedGroupKeys: grouped() ? selectedGroupKeys() : undefined,
      selectedEventIds: grouped() ? undefined : selectedEvents(),
    })
  );

  // The legend wraps, so its height changes with the colour mode — measured into the MAP/TABLE
  // height math rather than assumed.
  const [legendRowEl, setLegendRowEl] = createSignal<HTMLDivElement | undefined>(undefined);
  createEffect(() => {
    const el = legendRowEl();
    if (!el) return;
    const ro = new ResizeObserver(() => { updateContainerHeights(); });
    ro.observe(el);
    onCleanup(() => ro.disconnect());
  });

  return (
    <>
      <div
        id="media-container"
        class={`maneuvers-page${showTimeline() && hasFilteredManeuvers() ? ' maneuvers-page--with-selection-timeline' : ''}`}
        data-fleet-maneuvers-history-page
      >
        <div class="container">
          {/* Header section */}
          <div id="maneuver-controls" class="flex flex-col w-full pl-2 pt-2">
            {/* Top Control Bar: single row */}
            <div class="maneuver-control-bar-row flex w-full h-[50px] items-center gap-x-2">
              {/* Left group: 4 buttons */}
              <div class="flex gap-x-2 items-center flex-shrink-0">
                <div class="self-center">
                  <ManeuverSettings
                    useIconTrigger={true}
                    isHistoryPage={true}
                    raceOptions={raceOptions()}
                    legOptions={legOptions()}
                    gradeOptions={gradeOptions()}
                    stateOptions={stateOptions()}
                    selectedRaces={() => selectedRaces()}
                    selectedLegs={() => selectedLegs()}
                    selectedGrades={() => selectedGrades()}
                    selectedStates={() => selectedStates()}
                    selectedTrainingRacing={maneuverTrainingRacing()}
                    onTrainingRacingFilterChange={setManeuverTrainingRacing}
                    selectedTackEntry={maneuverTackEntry()}
                    onTackEntryFilterChange={setManeuverTackEntry}
                    onSelectBaseline={() => void baselineDisplay.openModal()}
                    baselineName={baselineDisplay.baselineLabel}
                    setRaceOptions={setRaceOptions}
                    setLegOptions={setLegOptions}
                    setGradeOptions={setGradeOptions}
                    setStateOptions={setStateOptions}
                    toggleRaceFilter={toggleRaceFilter}
                    toggleLegFilter={toggleLegFilter}
                    toggleGradeFilter={toggleGradeFilter}
                    toggleStateFilter={toggleStateFilter}
                    filterYear={filterYear}
                    filterEvent={filterEvent}
                    filterConfig={filterConfigValue}
                    onFilterYearChange={setFilterYear}
                    onFilterEventChange={setFilterEvent}
                    onFilterConfigChange={setFilterConfigValue}
                    onApplyFilters={() => fetchTableData()}
                    useUnfilteredOptions={true}
                    dataSourcesOptions={dataSourcesOptions()}
                    showTimeline={() => showTimeline()}
                    onTimelineChange={handleTimelineChange}
                    componentConfig={{
                      showGrades: true,
                      showRaces: true,
                      showLegs: true,
                      showStates: true,
                      showTackEntry: true,
                    }}
                  />
                </div>
                <DropDownButton 
                  options={eventTypes()}
                  defaultText={eventType()}
                  smallLabel="Maneuver"
                  size="big"
                  handleSelection={handleEventType}
                />
                <DropDownButton
                  options={viewOptions()}
                  defaultText={grouped() && view() === 'SCATTER' ? 'BOXES' : view()}
                  smallLabel="View"
                  size="big"
                  handleSelection={handleView}
                />
                <Show when={view() === 'TIME SERIES' || view() === 'MAP'}>
                  <DropDownButton
                    options={phases()}
                    defaultText={phase()}
                    smallLabel="Phase"
                    size="big"
                    handleSelection={handlePhase}
                  />
                </Show>
                {/* Active Filters: right side of left button area */}
                <div class="maneuver-panel-filter-summary">
                  <PerformanceFilterSummary
                    filterGrades={selectedGrades().join(",")}
                    filterState={selectedStates().join(",")}
                    stateOptions={stateOptions()}
                    trainingRacing={maneuverTrainingRacing()}
                    tackEntry={maneuverTackEntry()}
                    gradeAsGreaterThan
                  />
                </div>
              </div>

              {/* Right button area: TWS, Grouped, Data, Color By (unchanged) */}
              <div class="maneuver-control-bar-right flex gap-x-2 items-center ml-auto flex-shrink-0">
                <DropDownButton
                  options={twsoptions()}
                  defaultText={tws()}
                  smallLabel="Tws"
                  size="small"
                  handleSelection={handleTws}
                />
                <DropDownButton
                  options={groupedOptions()}
                  defaultText={groupDisplayMode()}
                  smallLabel="Group"
                  size="small"
                  handleSelection={(val) => { setGroupDisplayMode(val); setTriggerUpdate(true); }}
                />
                <Show when={view() === 'MAP' || view() === 'TIME SERIES' || view() === 'SCATTER'}>
                  <DropDownButton
                    options={colors()}
                    defaultText={color()}
                    smallLabel="Color By"
                    size="big"
                    handleSelection={handleColor}
                  />
                </Show>
              </div>
            </div>
            
            <Show when={showTimeline() && hasFilteredManeuvers()}>
              <div class="maneuver-selection-timeline">
                <ScatterTimeseries
                  key={`fleet-maneuver-tl-hist-${maneuverTimelinePoints().length}-${color()}-${tws()}-${eventType()}`}
                  aggregates={maneuverTimelinePoints()}
                  color={color()}
                  groups={maneuverTimelineGroups()}
                  isHistoryPage={true}
                  showUwDw={false}
                  skipSelectedSourcesFilter={true}
                  colorScale={
                    color() === 'SOURCE' && fleetManeuverSourceColorScale()
                      ? (source: string) => String(fleetManeuverSourceColorScale()!(source))
                      : undefined
                  }
                />
              </div>
            </Show>
            {/* Shared color-by legend for all views. Deliberately outside the strip's Show: it
                stays visible when the Timeline toggle is off, so MAP/TABLE keep their legend. */}
            <Show when={hasFilteredManeuvers() && maneuverColorLegendItems().length > 0}>
              <div
                id="maneuver-color-legend-row"
                class="maneuver-color-legend-row"
                ref={setLegendRowEl}
              >
                <ManeuverColorLegend
                  colorMode={color()}
                  items={maneuverColorLegendItems()}
                  onItemClick={handleLegendClick}
                  onBaselineClick={() => void baselineDisplay.openModal()}
                />
              </div>
            </Show>
          </div>
        {/* Content section */}
        <Show when={dataLoaded() && hasManeuvers() && !hasFilteredManeuvers()}>
          <div class="flex items-center justify-center h-[calc(100vh-200px)]">
            <div class="text-center">
              <p class="text-xl text-gray-600">No maneuvers found</p>
              <p class="text-sm text-gray-500 mt-1">Filtering criteria may be too strict, check your settings...</p>
            </div>
          </div>
        </Show>
        <Show when={hasFilteredManeuvers()}>
          <Show when={view() === 'MAP'}>
            <div>
              <div
                id="maneuver-map-layout"
                class="maneuver-map-layout flex w-full"
                classList={{ 'maneuver-map-layout--chart-panel': mapSidePanel() === 'CHART' }}
              >
                <div id="table-area">
                  <div class="fleet-maneuver-history-table-shell">
                    <div class="fleet-maneuver-history-table-shell__scroll">
                      <Show when={!grouped()}>
                        <Show when={mapSidePanel() === 'TABLE'}>
                          <DataTable_Small context={context} />
                        </Show>
                        <Show when={mapSidePanel() === 'CHART'}>
                          <div style={{ height: "100%", display: "flex", "flex-direction": "column", "min-height": "0" }}>
                            <div style={{ flex: "1 1 auto", "min-height": "0" }}>
                              <TimeSeries context={context} selectedOnly playhead fitParent />
                            </div>
                          </div>
                        </Show>
                      </Show>
                      <Show when={grouped()}>
                        <DataTable_SmallGrouped context={context} />
                      </Show>
                    </div>
                    {/* Toggle sits below the scroll area, above the footer text, so the footer stays put. */}
                    <Show when={!grouped()}>
                      <div class="flex justify-start mt-2">
                        <Show when={mapSidePanel() === 'TABLE' && selectedEvents().length > 0}>
                          <button
                            class="btn"
                            type="button"
                            onClick={() => setMapSidePanel('CHART')}
                          >
                            Show Timeseries →
                          </button>
                        </Show>
                        <Show when={mapSidePanel() === 'CHART'}>
                          <button class="btn" type="button" onClick={() => setMapSidePanel('TABLE')}>
                            ← Show Data Table
                          </button>
                        </Show>
                      </div>
                    </Show>
                    <div class="fleet-maneuvers-footer-text fleet-maneuvers-footer-text--under-table">
                      <p>
                        Select a TWS bin to see the top maneuvers for that wind speed, or choose ALL to see the best port and starboard maneuver for every bin and each Color By group simultaneously
                      </p>
                    </div>
                  </div>
                </div>
                <div id="map-area">
                  <Show when={!grouped()}>
                    <Map
                      context={context}
                      filterYear={filterYear}
                      filterEvent={filterEvent}
                      filterConfig={filterConfigValue}
                      filterState={() => selectedStates().join(',')}
                    />
                  </Show>
                  <Show when={grouped()}>
                    <MapGrouped
                      context={context}
                      filterYear={filterYear}
                      filterEvent={filterEvent}
                      filterConfig={filterConfigValue}
                      filterState={() => selectedStates().join(',')}
                    />
                  </Show>
                </div>
              </div>
            </div>
          </Show>
          <Show when={view() === 'SCATTER'}>
            <div id="scatter-area">
              <Show when={!grouped()}>
                <Scatter context={context} />
              </Show>
              <Show when={grouped()}>
                <ScatterGrouped context={context} />
              </Show>
            </div>
          </Show>
          <Show when={view() === 'TIME SERIES'}>
            <div id="timeseries-area">
              <Show when={!grouped()}>
                <TimeSeries context={context} onDataUpdate={fetchTableData} />
              </Show>
              <Show when={grouped()}>
                <TimeSeriesGrouped context={context} onDataUpdate={fetchTableData} />
              </Show>
            </div>
          </Show>
          <Show when={view() === 'TABLE'}>
            <div id="datatable-big-wrapper">
              <Show when={!grouped()}>
                <DataTable_Big
                  context={context}
                  hideClearFormatting={true}
                  onClearFormattingReady={(api) => setFormattingControl(api)}
                />
              </Show>
              <Show when={grouped()}>
                <DataTable_BigGrouped
                  context={context}
                  hideClearFormatting={true}
                  onClearFormattingReady={(api) => setFormattingControl(api)}
                />
              </Show>
            </div>
            <Show when={formattingControl()}>
              <div class="flex justify-end mt-2 px-4">
                <button class="btn" type="button" onClick={() => formattingControl()?.toggle()}>
                  {formattingControl()!.hasFormatting ? "Clear Formatting" : "Add formatting"}
                </button>
              </div>
            </Show>
          </Show>
        </Show>
        </div>
      </div>

      <BaselineDisplayModal
        show={baselineDisplay.showModal()}
        onClose={baselineDisplay.closeModal}
        title="Select Baseline"
        baselines={baselineDisplay.availableBaselines()}
        selectedKey={baselineDisplay.selectedKey()}
        onSelectedKeyChange={baselineDisplay.setSelectedKey}
        configs={baselineDisplay.modalConfigs()}
        selectedConfig={baselineDisplay.modalConfig()}
        onSelectedConfigChange={baselineDisplay.setModalConfig}
        baselineColor={baselineDisplay.modalColor()}
        onBaselineColorChange={baselineDisplay.setModalColor}
        loading={baselineDisplay.loading()}
        loadingAction={baselineDisplay.loadingAction()}
        error={baselineDisplay.error()}
        onConfirm={() => void baselineDisplay.handleConfirm()}
        onRemove={() => void baselineDisplay.handleRemove()}
      />

      <BaselineSaveModal
        show={baselineSave.showSaveModal()}
        onClose={baselineSave.closeSaveModal}
        baselines={baselineSave.baselines()}
        selectedKey={baselineSave.selectedKey()}
        onSelectedKeyChange={baselineSave.setSelectedKey}
        isNamingNew={baselineSave.isNamingNew()}
        newName={baselineSave.newName()}
        onNewNameChange={baselineSave.setNewName}
        selectedEventCount={baselineSave.selectedEventCount()}
        saving={baselineSave.saving()}
        saveError={baselineSave.saveError()}
        saveMessage={baselineSave.saveMessage()}
        onSave={() => void baselineSave.handleSave()}
      />

      {/* Explains why + did nothing, e.g. grouped mode or no selection. */}
      <Show when={baselineSave.hotkeyNotice()}>
        <div class="baseline-hotkey-notice" onClick={baselineSave.dismissNotice}>
          {baselineSave.hotkeyNotice()}
        </div>
      </Show>
    </>
  );
}
